/**
 * Extract the font family name from a raw TTF/OTF (SFNT) file by walking the
 * binary `name` table. Returns `null` if the file isn't a recognizable SFNT or
 * no usable record was found — callers should fall back to a filename guess.
 *
 * Spec: https://learn.microsoft.com/en-us/typography/opentype/spec/name
 *
 * Preference order:
 *   1. nameID 16 "Typographic Family" — what designers think of as the family
 *      (e.g. "Inter" rather than "Inter Semi Bold").
 *   2. nameID 1 "Family Name" — the legacy family.
 *
 * Within each, prefer the Windows / English record (platformID 3, langID 0x409)
 * because that's the canonical encoding fonts ship with; fall back to anything.
 *
 * Does NOT support WOFF / WOFF2 — those wrap SFNT in zlib / brotli. Restrict
 * uploads to .ttf / .otf at the UI layer.
 */
export function parseFontFamily(buf: ArrayBuffer): string | null {
    if (buf.byteLength < 12) return null;
    const v = new DataView(buf);

    // SFNT header: version (4 bytes) + numTables (2). Valid versions:
    //   0x00010000 = TrueType
    //   0x4F54544F = 'OTTO' (OpenType / CFF)
    const sfntVersion = v.getUint32(0, false);
    if (sfntVersion !== 0x00010000 && sfntVersion !== 0x4f54544f) return null;

    const numTables = v.getUint16(4, false);
    // Each table directory entry is 16 bytes: tag (4) + checksum (4) + offset (4) + length (4).
    const NAME_TAG = 0x6e616d65; // 'name'
    let nameOffset = 0;
    let nameLength = 0;
    for (let i = 0; i < numTables; i++) {
        const entry = 12 + i * 16;
        if (entry + 16 > v.byteLength) return null;
        if (v.getUint32(entry, false) === NAME_TAG) {
            nameOffset = v.getUint32(entry + 8, false);
            nameLength = v.getUint32(entry + 12, false);
            break;
        }
    }
    if (
        !nameOffset ||
        nameLength < 6 ||
        nameOffset + nameLength > buf.byteLength
    ) {
        return null;
    }

    // Name table header: format (2) + count (2) + stringOffset (2).
    const count = v.getUint16(nameOffset + 2, false);
    const stringStart = nameOffset + v.getUint16(nameOffset + 4, false);

    type Rec = { platformId: number; offset: number; length: number };
    let englishId16: Rec | null = null;
    let anyId16: Rec | null = null;
    let englishId1: Rec | null = null;
    let anyId1: Rec | null = null;

    for (let i = 0; i < count; i++) {
        const off = nameOffset + 6 + i * 12;
        if (off + 12 > nameOffset + nameLength) break;
        const platformId = v.getUint16(off, false);
        const languageId = v.getUint16(off + 4, false);
        const nameId = v.getUint16(off + 6, false);
        const length = v.getUint16(off + 8, false);
        const stringOffset = v.getUint16(off + 10, false) + stringStart;
        if (nameId !== 1 && nameId !== 16) continue;

        const rec: Rec = { platformId, offset: stringOffset, length };
        const isEnglish =
            (platformId === 3 &&
                (languageId === 0x0409 || languageId === 0)) ||
            (platformId === 1 && languageId === 0) ||
            platformId === 0; // Unicode platform — language-neutral

        if (nameId === 16) {
            if (isEnglish && !englishId16) englishId16 = rec;
            if (!anyId16) anyId16 = rec;
        } else {
            if (isEnglish && !englishId1) englishId1 = rec;
            if (!anyId1) anyId1 = rec;
        }
    }

    const chosen = englishId16 ?? anyId16 ?? englishId1 ?? anyId1;
    if (!chosen) return null;
    if (chosen.offset + chosen.length > buf.byteLength) return null;

    const bytes = new Uint8Array(buf, chosen.offset, chosen.length);
    // Windows (3) and Unicode (0) platforms store strings as UTF-16BE.
    // Mac Roman (1) is technically its own encoding; ASCII is close enough
    // for font-family names which almost always use Latin-1 characters.
    let decoded: string;
    if (chosen.platformId === 3 || chosen.platformId === 0) {
        decoded = new TextDecoder("utf-16be").decode(bytes);
    } else {
        let s = "";
        for (let i = 0; i < bytes.length; i++) {
            const c = bytes[i]!;
            if (c >= 0x20 && c < 0x7f) s += String.fromCharCode(c);
        }
        decoded = s;
    }
    return decoded.trim() || null;
}
